import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Calculator, Send, CheckCircle2, Copy, FileText, AlertTriangle, Bot } from 'lucide-react';
import { api } from '../lib/api';
import Modal from '../components/ui/Modal';
import CopilotDock from '../components/CopilotDock';
import Radios from '../components/quote-builder/Radios';
import { Changed, changedRing } from '../components/quote-builder/Changed';
import ScopePanel from '../components/quote-builder/ScopePanel';
import LinesTable from '../components/quote-builder/LinesTable';
import BasisPanel from '../components/quote-builder/BasisPanel';
import CostInputsPanel from '../components/quote-builder/CostInputsPanel';
import CustomerPreview from '../components/quote-builder/CustomerPreview';
import MeetingReview from '../components/quote-builder/MeetingReview';
import {
  formLine, loadedForm, reseedScope, reseedCosting, initState, copilotChange, undoBlock, markUndone, canUndo, edit, editLine, addLine, removeLine, toolsChoice, setToolsChoice,
  synced, isDirty, linesForSave, fieldsForSave, scopeForSave, costingForSave, tagSource, builderViewRequest, viewFor, viewLayout, runUndo, keepsBudgetary, UNDO_KEEPS_BUDGETARY, offerOptions,
} from '../../modules/q2c/quote-builder-state.mjs';
import { meetingIntakeOn, initialChoices, acceptedRows, crmDraft, pricingPicks, withFreshDefaults } from '../../modules/copilot/meeting-review.mjs';
import { dockKey, dockTurn, beforeCopilotSave, undoGate } from '../../modules/copilot/dock-state.mjs';
import { easternDay } from '../../modules/q2c/eastern-day.mjs';
import { countWord } from '../lib/countWord.mjs';
import { usd, fmtNum, StatusChip, ErrorBox, Labeled, openAuthed, mulCents, parseNum, inputCls, btnCls, btn2Cls } from '../lib/q2c';

const EDITABLE = ['draft', 'approved'];
const HEADER_KEYS = ['kind', 'title', 'account_id', 'contact_id', 'customer_name', 'bill_to', 'recipient_name', 'recipient_email', 'bid_due_date', 'validity_days', 'tax_mode', 'tax_rate_pct', 'narrative', 'terms_text', 'internal_notes', 'owner_email'];
// LP-394 (schema-d): offered only once the server says those columns exist.
const EXT_KEYS = ['offer_type', 'scope_text'];
const keysFor = (d) => (d?.ext_columns === true ? [...HEADER_KEYS, ...EXT_KEYS] : HEADER_KEYS);
const TWO_PARTNERS_OVER = 25000; // partner ruling 2026-10-10 (quote-service.js APPROVAL_RULE)

let uid = 0;
const newLine = (over = {}) => formLine({ id: `n${Date.now().toString(36)}${(uid += 1)}`, ...over });
// Round a price up to the cent without float drift (1.12 stays 1.12, 1.1201 → 1.13).
const ceilCents = (x) => Math.ceil(Number((Number(x) * 100).toFixed(6))) / 100;
const lineQty = (l) => (l.pricing === 'per_sf' ? l.area_sf : l.pricing === 'lump' ? 1 : l.qty);
// Same exact decimal rounding as the server, so what is shown is what gets saved and sent.
const lineAmount = (l) => (Number.isFinite(parseNum(lineQty(l))) && Number.isFinite(parseNum(l.unit_price)) ? mulCents(lineQty(l), l.unit_price) : 0);
const pct = (n) => (n == null ? '—' : `${Number(n).toFixed(1)}%`);
// The builder view's error in words: a 403 is the catalog permission it needs (a reload does not help).
const viewErrorText = (e) => (e?.status === 403 ? 'Costs and margins need read access to Catalog; ask an admin for it.' : e?.message || 'failed');

/**
 * The quote builder (LP-402): the quote form on the left, the Copilot docked on the right (a drawer on
 * phones). A Copilot change fills in the form in place, highlighted and tagged "Changed by Copilot",
 * with an Undo per change (modules/q2c/quote-builder-state.mjs); typing and clicking work as before.
 * Scope first (area and coverage set every quantity), then the lines with expected cost, true cost,
 * sell, margin and a check, the cost inputs (LP-424: tariff, FX, freight model, Concria's offer), the
 * pricing basis, the terms, and Approve / Send (a partner's click; over
 * $25,000 two partners). "Customer preview" shows the quote exactly as the customer sees it.
 * The numbers come from the server (GET/POST /api/quotes/:id/builder); a quote the builder made saves
 * through it (PUT), any other quote through the Quote editor's PATCH.
 */
export default function QuoteEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [st, setSt] = useState(null); // the form and its Copilot changes (quote-builder-state.mjs)
  // The builder view (costs, margins, checks), the request it answers and its revision: { key, view,
  // revision }. It is shown only while its key is the form's as it is now (viewFor): never for an older
  // revision, basis or form. A request or a failure invalidates it (key null): its numbers go; its layout
  // (which lines the scope sets) stays only while the quote is still at its revision.
  const [viewed, setViewed] = useState(null);
  const viewKey = useRef('');
  const invalidateView = useCallback(() => { setViewed((v) => (v ? { ...v, key: null } : v)); viewKey.current = ''; }, []);
  const [viewPending, setViewPending] = useState(false);
  const [viewErr, setViewErr] = useState('');
  const [basis, setBasis] = useState('true');
  const [tab, setTab] = useState('ours');
  const [dockOpen, setDockOpen] = useState(false);
  const [openLine, setOpenLine] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState(null);
  const [products, setProducts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loadNotes, setLoadNotes] = useState([]);
  const [confirmSend, setConfirmSend] = useState(false);
  const [convert, setConvert] = useState(false);
  const [pricing, setPricing] = useState(0); // catalog-pricing requests in flight
  const [editScope, setEditScope] = useState(false);
  const [livePreview, setLivePreview] = useState(null); // terms preview for unsaved offer type / terms
  // LP-403 "Start from a meeting" (only when the server says it is switched on): the Copilot's reading,
  // the partner's Accept / Edit / Skip per row, and what the dock is told after an Accept.
  const [meeting, setMeeting] = useState(null);
  const [meetChoices, setMeetChoices] = useState({ meeting: {}, web: {}, pricing: {} });
  const [meetEdits, setMeetEdits] = useState({});
  const [meetMsg, setMeetMsg] = useState({ meeting: null, web: null });
  const [meetFeed, setMeetFeed] = useState([]);

  const seq = useRef(0);
  const routeId = useRef(id);
  routeId.current = id;
  const stRef = useRef(st);
  stRef.current = st;
  const dataRef = useRef(data);
  dataRef.current = data;
  const basisRef = useRef(basis);
  basisRef.current = basis;

  /**
   * The quote, its builder view and the form for it (nothing is set here). When the view fails, the
   * form has no scope (a save then sends none, so the server keeps the quote's own scope, never an
   * older one), the error is said, and the view is retried: its scope comes back with it (reseedScope).
   */
  const fetchQuote = useCallback(async () => {
    const d = await api.get(`/quotes/${id}`);
    const b = basisRef.current;
    let v = null; let viewError = '';
    try { v = (await api.get(`/quotes/${id}/builder?basis=${b}`)).view; } catch (e) { viewError = viewErrorText(e); }
    return { d, v, basis: b, viewError, next: loadedForm(d.quote, v, { keys: keysFor(d) }) };
  }, [id]);

  const apply = useCallback((got, nextState) => {
    setData(got.d);
    if (got.v) {
      // Keyed to the basis it was fetched with (a basis picked meanwhile asks for its own view).
      const { key } = builderViewRequest(null, { revision: got.d.quote.updated_at, basis: got.basis, unsaved: false });
      setViewed({ key, view: got.v, revision: got.d.quote.updated_at }); setViewErr('');
      viewKey.current = key;
    } else { invalidateView(); setViewErr(got.viewError); }
    setSt(nextState);
    setEditScope(false); setError('');
  }, [invalidateView]);

  /** Reload the quote. Resolves to the error's message when it failed (and was still the latest load), else null. */
  const load = useCallback(async () => {
    // A reload from an action on a document we've since navigated away from does nothing.
    if (id !== routeId.current) return null;
    const mine = ++seq.current;
    try {
      const got = await fetchQuote();
      if (mine !== seq.current || got.d.quote.id !== routeId.current) return null; // a newer load (or another version) won
      // Changes, their tags and lines left off survive a reload of the same quote.
      const prev = stRef.current;
      apply(got, prev && dataRef.current?.quote?.id === got.d.quote.id ? synced(prev, got.next) : initState(got.next));
      return null;
    } catch (e) {
      if (mine !== seq.current) return null;
      setError(e.message);
      return e.message || 'The quote did not reload.';
    }
  }, [id, fetchQuote, apply]);
  // Switching version/quote: drop the old document first so its fields and actions can't be used.
  useEffect(() => { setData(null); setSt(null); setViewed(null); viewKey.current = ''; setConfirmSend(false); setConvert(false); setLivePreview(null); setOpenLine(null); setMeeting(null); setMeetMsg({ meeting: null, web: null }); setMeetFeed([]); load(); }, [load]);
  // Keep each list failure visible once, even if an effect is replayed in development.
  const noteLoad = (text) => setLoadNotes((n) => (n.includes(text) ? n : [...n, text]));
  useEffect(() => {
    api.get('/quotes/meta').then(setMeta).catch(() => noteLoad('Could not load the partner list. Reload the page before changing the owner or converting to an order.'));
    api.get('/catalog/products').then((r) => setProducts(r.products || [])).catch(() => noteLoad('Could not load the catalog products. Reload the page before picking a product on a line.'));
    api.get('/crm/accounts').then((r) => setAccounts(r.accounts || r.data || [])).catch(() => noteLoad('Could not load the CRM companies. Reload the page before changing the account.'));
    api.get('/crm/contacts').then((r) => setContacts(r.contacts || r.data || [])).catch(() => noteLoad('Could not load the CRM contacts. Reload the page before changing the contact.'));
  }, []);

  const q = data && data.quote.id === id ? data.quote : null;
  const form = st?.form || null;
  const dirty = !!st && isDirty(st);
  // Locked while a save/action is in flight, so nothing typed meanwhile is silently replaced by the reload.
  const canEdit = !!q && EDITABLE.includes(q.status) && !q.send_ambiguous && data.is_latest;
  const editable = canEdit && !busy;
  const editableRef = useRef(editable);
  editableRef.current = editable;
  const productById = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const extOn = data?.ext_columns === true && !!form && 'offer_type' in form.fields;
  const builderMade = extOn && !!q?.pricing_basis;
  const meetingOn = meetingIntakeOn(data);
  const fields = form?.fields || {};
  const termsChanged = !!q && extOn && (String(fields.offer_type || '') !== String(q.offer_type || '') || String(fields.terms_text || '') !== String(q.terms_text || ''));
  const shownContacts = fields.account_id ? contacts.filter((c) => !c.account_id || c.account_id === fields.account_id) : contacts;
  const tagOf = (p) => st?.tags[p];
  const T = (p) => <Changed id={tagOf(p)} source={tagSource(st, p)} key={`${p}${tagOf(p) || ''}`} />;

  // What the customer will read in Terms & Notes for the offer type / terms being edited.
  useEffect(() => {
    setLivePreview(null);
    if (!termsChanged || !editable) return undefined;
    let dead = false;
    const t = setTimeout(() => {
      api.post('/quotes/terms-preview', { offer_type: fields.offer_type || null, terms_text: fields.terms_text })
        .then((r) => { if (!dead) setLivePreview(r); })
        .catch((e) => { if (!dead) setLivePreview({ error: e.message }); });
    }, 400);
    return () => { dead = true; clearTimeout(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termsChanged, !!editable, fields.offer_type, fields.terms_text]);

  // The numbers (quantities, costs, margins, checks) for the form as it is: the server's builder view.
  // Each request first invalidates the view it replaces, and a failed one leaves it invalid: the numbers on
  // screen are only ever the answer for this revision, basis and form (viewFor below).
  useEffect(() => {
    if (!st || !q) return undefined;
    const unsaved = dirty && canEdit;
    const { key, body } = builderViewRequest(st, { revision: q.updated_at, basis, unsaved });
    if (key === viewKey.current) return undefined;
    invalidateView();
    let dead = false;
    const t = setTimeout(async () => {
      setViewPending(true);
      try {
        const r = unsaved ? await api.post(`/quotes/${id}/builder`, body) : await api.get(`/quotes/${id}/builder?basis=${basis}`);
        if (!dead) {
          setViewed({ key, view: r.view, revision: q.updated_at }); setViewErr(''); viewKey.current = key;
          // The saved quote's view: the form takes its scope and cost inputs while nothing is unsaved (after a failed load).
          if (!unsaved) setSt((s) => reseedCosting(reseedScope(s, r.view.scope), r.view.costing?.form || null));
        }
      } catch (e) { if (!dead) { invalidateView(); setViewErr(viewErrorText(e)); } } finally { if (!dead) setViewPending(false); }
    }, unsaved ? 350 : 0);
    return () => { dead = true; clearTimeout(t); setViewPending(false); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [st?.form, basis, q?.updated_at, canEdit]);

  const change = (path, value) => setSt((s) => edit(s, path, value));
  const setF = (k) => (e) => change(`fields.${k}`, e && e.target ? e.target.value : e);

  /** Store a form state (no reload): through the builder for a quote it made, else the Quote editor's PATCH. Resolves to the server's answer ({ quote }). */
  async function putState(s, { reason, destination } = {}) {
    if (pricing > 0) throw new Error('Wait for the catalog pricing to finish.');
    const cur = dataRef.current.quote;
    const body = fieldsForSave(s.form, s.saved);
    if (builderMade) return api.put(`/quotes/${id}/builder`, { revision: cur.updated_at, fields: body, scope: scopeForSave(s.form.scope), costing: costingForSave(s.form.costing), lines: linesForSave(s.form.lines), ...(reason ? { reason } : {}), ...(destination !== undefined ? { destination } : {}) });
    return api.patch(`/quotes/${id}`, { ...body, lines: linesForSave(s.form.lines), revision: cur.updated_at });
  }
  /** Save a form state, then reload the quote. */
  async function saveState(s, opts) {
    await putState(s, opts);
    await load();
  }

  async function save() {
    if (pricing > 0) return; // wait for catalog pricing to land first
    setBusy(true); setError(''); setNotice('');
    try { await saveState(stRef.current); setNotice('Saved.'); } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  async function act(path, body, okMsg) {
    setBusy(true); setError(''); setNotice('');
    try { const r = await api.post(`/quotes/${id}${path}`, body || {}); await load(); if (okMsg) setNotice(okMsg); return r; } catch (e) { setError(e.message); throw e; } finally { setBusy(false); }
  }

  async function approve() {
    try {
      await act('/approve', { revision: q.updated_at }, 'Approved. Review the recipients, then send.');
    } catch (e) {
      // eslint-disable-next-line no-alert
      if (/below cost/i.test(e.message) && window.confirm(`${e.message}\n\nApprove anyway?`)) {
        await act('/approve', { revision: q.updated_at, acknowledge_warnings: true }, 'Approved (below-cost acknowledged).').catch(() => {});
      }
    }
  }

  // ── Copilot (docked) ──
  /**
   * Before a message: save what was typed, so the Copilot changes what is on the screen. On an approved
   * quote that save returns it to draft, so it happens only once the partner confirms (LP-443,
   * dock-state.mjs beforeCopilotSave); otherwise nothing is saved, the quote stays approved and the
   * message is held. Resolves to the reason it was held, or null.
   */
  async function beforeCopilot() {
    setBusy(true); setError('');
    try {
      const s = stRef.current;
      const held = await beforeCopilotSave({
        status: q.status, canEdit, dirty: !!s && isDirty(s), label: `${q.number} V${q.version}`,
        // eslint-disable-next-line no-alert
        confirm: (question) => window.confirm(question),
        save: () => saveState(s),
      });
      if (held) setBusy(false);
      return held;
    } catch (e) { setBusy(false); setError(e.message); throw e; }
  }
  /** After an answer: the form follows what the Copilot saved on this quote, tagged, with an Undo. */
  async function afterCopilot(res, message, { failed } = {}) {
    try {
      const card = [...(res?.drafts || [])].reverse().find((x) => x.kind === 'quote' && x.id === id)?.quote || null;
      if (!card && !failed) return null; // nothing on this quote (a question, another quote)
      const before = dataRef.current?.quote?.updated_at;
      const got = await fetchQuote();
      if (got.d.quote.id !== routeId.current) return null;
      if (!card && got.d.quote.updated_at === before) return null;
      const lines = card?.changes || [];
      const r = copilotChange(stRef.current, got.next, { id: `c${Date.now().toString(36)}`, label: message, lines });
      apply(got, r.state);
      return r.change ? { id: r.change.id, lines } : null;
    } catch (e) { setError(e.message); return null; } finally { setBusy(false); }
  }
  /**
   * Undo one Copilot change (quote-builder-state.mjs runUndo): the form with its fields put back is
   * saved, and only once that save succeeded is the change marked undone. When the save fails (409 or
   * 400) the form stays as it was before the Undo, and the error is said. When the save succeeded but
   * the reload failed, the change is undone all the same (the form shows what was saved), the reload is
   * tried again, and if it still fails the page says to reload before editing.
   */
  async function undoChange(changeId) {
    const pre = stRef.current;
    // Undo is for drafts (LP-442): on an approved quote its save would withdraw the approval with no prompt.
    const why = undoGate({ status: q.status, canEdit }) || undoBlock(pre, changeId, destOf());
    if (why) throw new Error(why);
    setBusy(true); setError(''); setNotice('');
    try {
      // A change accepted from a meeting that moved the destination puts the one it replaced back (LP-403).
      const ch = pre.changes.find((c) => c.id === changeId);
      const dest = ch?.destination_before !== undefined ? { destination: ch.destination_before } : {};
      const r = await runUndo(pre, changeId, {
        save: (form) => putState({ ...pre, form }, { reason: 'undo', ...dest }),
        reload: load,
        wait: () => new Promise((ok) => { setTimeout(ok, 1500); }),
      });
      if (!r.saved) { setSt(pre); setError(r.error); throw new Error(r.error); }
      if (r.reloaded) setSt((s) => markUndone(s, changeId));
      else {
        // The saved quote's revision comes from the save's answer, so no view or preview of the revision
        // before the Undo is shown with the undone form; their numbers come back when they load for it.
        const row = r.result?.quote;
        if (row && row.id === id) setData((d) => (d && d.quote.id === row.id ? { ...d, quote: { ...d.quote, ...row, display_status: row.status } } : d));
        invalidateView();
        setSt(r.state);
        setError(`Undone and saved, but the quote did not reload (${r.error}). Reload the page to see everything as saved.`);
      }
      // LP-442 r2: the Undo of a change that turned the quote budgetary keeps it budgetary (only Make firm turns it firm).
      setNotice(keepsBudgetary(pre, changeId) ? `Undone and saved. ${UNDO_KEEPS_BUDGETARY}` : 'Undone and saved.');
    } finally { setBusy(false); }
  }

  // ── Start from a meeting (LP-403) ──
  /** The quote's destination now (a meeting change's Undo is off once it moved again). */
  const destOf = () => ({ destination: dataRef.current?.quote?.pricing_basis?.inputs?.destination ?? '' });

  /** The dock read a meeting: the Copilot's reading shows at the top of Our view; nothing is in the quote yet. */
  async function readMeeting(input) {
    if (input.bad) throw new Error('Upload the transcript as a .txt, .docx or .pdf file.');
    let r;
    if (input.file) {
      const fd = new FormData(); fd.append('file', input.file); fd.append('quote_id', id);
      const res = await fetch('/api/copilot/meeting', { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('mickey_token')}` }, body: fd });
      // An expired session goes to sign-in, as every api.js call and the pasted-text read do.
      if (res.status === 401) {
        localStorage.removeItem('mickey_token');
        window.location.href = '/login';
        throw new Error('Unauthorized');
      }
      r = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(r.error || `The upload failed (${res.status})`);
    } else r = await api.post('/copilot/meeting', { text: input.text, quote_id: id });
    setMeeting(r);
    // A blank quote's pricing inputs start unpicked: the partner picks each one before the draft (#48).
    setMeetChoices({ meeting: initialChoices(r.meeting.rows), web: initialChoices(r.web.rows), pricing: {} });
    setMeetEdits({}); setMeetMsg({ meeting: null, web: null }); setTab('ours'); setDockOpen(false);
    return r.summary;
  }

  /**
   * A partner's Accept: the checked rows (as read, or as edited) go into the quote through the server
   * (drafts only), what typed edits were unsaved is saved first, and the form follows: the fields that
   * moved are tagged "From the meeting" / "From the web: check this", with an Undo in the dock. On an
   * empty quote the checked facts draft the budgetary quote (both panels' checked rows).
   */
  async function acceptMeeting(part) {
    const rv = meeting;
    if (!rv) return;
    const draft = rv.mode === 'draft';
    const rows = {
      ...(part === 'meeting' || draft ? { meeting: acceptedRows(rv.meeting.rows, meetChoices.meeting, meetEdits) } : {}),
      ...(part === 'web' || draft ? { web: acceptedRows(rv.web.rows, meetChoices.web, meetEdits) } : {}),
      ...(draft ? { pricing: pricingPicks(rv, meetChoices.pricing, meetEdits) } : {}),
    };
    setBusy(true); setError(''); setNotice(''); setMeetMsg((m) => ({ ...m, [part]: null }));
    try {
      const s = stRef.current;
      if (s && canEdit && isDirty(s)) await saveState(s);
      const before = dataRef.current.quote;
      const out = await api.post('/copilot/meeting/apply', { quote_id: id, revision: before.updated_at, ...rows });
      const got = await fetchQuote();
      if (got.d.quote.id !== routeId.current) return;
      const skipped = (out.skipped || []).map((x) => `${x.label}: ${x.why}`);
      if (out.mode === 'draft') {
        apply(got, initState(got.next));
        // The review now fills this quote in (the web facts can be used, or changed, from here); the rows
        // that were pricing inputs of the draft are ordinary rows again.
        setMeeting((m) => (m ? { ...m, mode: 'fill', meeting: { ...m.meeting, rows: m.meeting.rows.map((x) => ({ ...x, pricing: false })) } } : m));
        setMeetMsg((m) => ({ ...m, meeting: { ok: true, text: `Drafted ${got.d.quote.number} as a budgetary quote from the checked facts: ${usd(got.d.quote.total)}. Check the price, customer and terms; it has not been sent.${[...(out.assumed || []), ...skipped].map((x) => ` ${x}`).join('')}` } }));
        setMeetFeed((f) => [...f, dockTurn(got.d.quote, 'assistant', `Drafted the budgetary quote from the meeting: ${usd(got.d.quote.total)}. Freight is estimated for the site. Nothing was sent or approved.`)]);
        return;
      }
      const label = part === 'web' ? 'From the web' : 'From the meeting';
      const r = copilotChange(stRef.current, got.next, {
        id: `m${Date.now().toString(36)}`, label, lines: out.applied || [], source: part,
        destination: { before: before.pricing_basis?.inputs?.destination ?? '', after: got.d.quote.pricing_basis?.inputs?.destination ?? '' },
      });
      apply(got, r.state);
      const n = (out.applied || []).length;
      setMeetMsg((m) => ({ ...m, [part]: { ok: true, text: `${n ? `${n} fact${n === 1 ? '' : 's'} went into the quote: ${out.applied.join(', ')}.` : 'Nothing on the quote changed; the facts are in the internal notes.'} Each changed field is tagged "${part === 'web' ? 'From the web: check this' : 'From the meeting'}". Undo is in the Copilot.${skipped.length ? ` ${skipped.join(' ')}` : ''}` } }));
      const totalNow = got.d.quote.total;
      // On this version's thread (LP-443: dock-state.mjs dockTurn), with the change's Undo.
      setMeetFeed((f) => [...f, dockTurn(got.d.quote, 'assistant',
        `${label}: ${n ? out.applied.join(', ') : 'nothing on the form moved (the facts are in the internal notes)'}. ${Number(totalNow) === Number(before.total) ? `The numbers didn't move: still ${usd(totalNow)}.` : `The total is now ${usd(totalNow)} (was ${usd(before.total)}).`}${part === 'web' ? ' A partner should check these before it goes out; nothing was saved to the CRM.' : ''}`,
        { change: r.change ? { id: r.change.id, lines: out.applied || [] } : null })]);
    } catch (e) {
      // A draft refused for its pricing inputs comes back with the Copilot's defaults as they are now:
      // the review shows them, and a "use the default" pick whose default changed is cleared, to pick again.
      if (draft && e.data && 'defaults' in e.data) {
        const fresh = withFreshDefaults(rv, e.data.defaults, meetChoices.pricing);
        setMeeting(fresh.review);
        setMeetChoices((c) => ({ ...c, pricing: fresh.choices }));
      }
      setMeetMsg((m) => ({ ...m, [part]: { ok: false, text: e.message } }));
    } finally { setBusy(false); }
  }

  /**
   * #48: the Copilot's defaults for the facts as checked now (product, area, site), when the review could
   * not work them out. It sends no pricing input as picked, so the server refuses the draft (400) and
   * writes nothing; its answer carries the defaults, which the review then shows for the partner to pick.
   */
  async function meetingDefaults() {
    const rv = meeting;
    if (!rv || rv.mode !== 'draft') return;
    setBusy(true); setMeetMsg((m) => ({ ...m, meeting: null }));
    try {
      await api.post('/copilot/meeting/apply', {
        quote_id: id, revision: dataRef.current.quote.updated_at, pricing: [],
        meeting: acceptedRows(rv.meeting.rows, meetChoices.meeting, meetEdits), web: acceptedRows(rv.web.rows, meetChoices.web, meetEdits),
      });
    } catch (e) {
      if (e.data?.defaults) {
        const fresh = withFreshDefaults(rv, e.data.defaults, meetChoices.pricing);
        setMeeting(fresh.review);
        setMeetChoices((c) => ({ ...c, pricing: fresh.choices }));
        setMeetMsg((m) => ({ ...m, meeting: { ok: true, text: 'The Copilot defaults for the checked product, area and site are shown. Pick each pricing input, then draft.' } }));
      } else setMeetMsg((m) => ({ ...m, meeting: { ok: false, text: e.message } }));
    } finally { setBusy(false); }
  }

  /** "Save to CRM": only after a partner confirms exactly what is created. */
  async function saveMeetingCrm() {
    const d = crmDraft(meeting, { meeting: meetChoices.meeting, web: meetChoices.web, edits: meetEdits });
    if (!d) return;
    const what = [
      d.creates.account ? `the account ${d.account.name}${d.account.phone ? `, ${d.account.phone}` : ''}${d.account.website ? `, ${d.account.website}` : ''}${d.account.hq_address ? `, HQ ${d.account.hq_address}` : ''}` : null,
      d.creates.contact ? `the contact ${d.contact.name}${d.contact.title ? ` (${d.contact.title})` : ''} at ${d.account.name}` : null,
    ].filter(Boolean).join('; and ');
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Save to the CRM: ${what}?\n\nOnly the facts you checked are saved.`)) return;
    setBusy(true); setMeetMsg((m) => ({ ...m, web: null }));
    try {
      const r = await api.post('/copilot/meeting/crm', { confirm: true, account: d.account, contact: d.contact });
      const [a, c] = await Promise.all([api.get('/crm/accounts').catch(() => null), api.get('/crm/contacts').catch(() => null)]);
      if (a) setAccounts(a.accounts || a.data || []);
      if (c) setContacts(c.contacts || c.data || []);
      setMeeting((m) => (m ? { ...m, web: { ...m.web, rows: m.web.rows.map((x) => (x.key === 'crm' ? { ...x, save: { account: false, contact: false }, note: null } : x)) } } : m));
      if (canEdit) {
        if (r.account?.id && !stRef.current.form.fields.account_id) change('fields.account_id', r.account.id);
        if (r.contact?.id) change('fields.contact_id', r.contact.id);
      }
      setMeetMsg((m) => ({ ...m, web: { ok: true, text: `Saved to the CRM: ${[r.account ? `${r.account.name}${r.account.created ? ' (new account)' : ' (already there)'}` : null, r.contact ? `${r.contact.name}${r.contact.created ? ' (new contact)' : ' (already there)'}` : null].filter(Boolean).join(', ')}. ${canEdit ? 'They are picked on the quote: Save to keep the link.' : ''}` } }));
    } catch (e) { setMeetMsg((m) => ({ ...m, web: { ok: false, text: e.message } })); } finally { setBusy(false); }
  }

  async function priceLine(lineId) {
    const l = stRef.current.form.lines.find((x) => x.id === lineId);
    if (!l) return;
    if (!l.product_id) { setError('Pick a catalog product first'); return; }
    const prod = productById[l.product_id];
    // The catalog prices one sale unit (bag, canister…): don't apply it to a line counted in something else.
    if (l.pricing === 'per_unit' && l.unit && prod && l.unit !== prod.sale_unit) {
      setError(`Catalog prices are per ${prod.sale_unit}; this line is in "${l.unit}". Clear the unit (or use ${prod.sale_unit}) to price from the catalog.`);
      return;
    }
    setPricing((n) => n + 1);
    try {
      const body = { product_id: l.product_id };
      const n = (v) => (Number.isFinite(parseNum(v)) ? parseNum(v) : v);
      if (l.pricing === 'per_sf') Object.assign(body, { area_sf: n(l.area_sf), dosage_lb_sf: n(l.dosage_lb_sf), waste_pct: n(l.waste_pct || 0) });
      else body.qty_units = n(l.qty || 1);
      // Includes the fields being filled: a price or cost typed while the request runs wins.
      const inputsOf = (x) => JSON.stringify([x.product_id, x.pricing, x.area_sf, x.dosage_lb_sf, x.waste_pct, x.qty, x.unit, x.unit_price, x.unit_cost]);
      const asked = inputsOf(l);
      const r = await api.post('/catalog/calc/price', body);
      if (r.status !== 'known') { setError(r.warnings.join(' ')); return; }
      const perSf = l.pricing === 'per_sf';
      const cost = perSf ? r.cost_per_sf : r.cost_per_unit;
      const sell = perSf ? r.sell_per_sf : r.sell_per_unit;
      if (!Number.isFinite(cost) || !Number.isFinite(sell)) {
        setError(`${r.sku}: no ${perSf ? 'per-sf' : 'per-unit'} price (the pack size is unknown). Enter the price by hand or set the pack size in Catalog.`);
        return;
      }
      // Apply to the CURRENT line, and only if its pricing inputs are still what was priced (edits made while the request ran are kept).
      setSt((s) => {
        const x = s.form.lines.find((y) => y.id === l.id);
        return x && inputsOf(x) === asked ? editLine(s, l.id, { unit_cost: cost, unit_price: ceilCents(sell) }) : s;
      });
      setNotice([`${r.sku}: cost ${usd(perSf ? r.cost_per_sf : r.cost_per_unit, 4)}, sell at ${r.markup_pct}% markup.`, ...r.warnings, ...r.floor_checks.map((c) => c.message)].join(' '));
    } catch (e) { setError(e.message); } finally { setPricing((n) => n - 1); }
  }

  if (error && !data) return <div className="mx-auto max-w-5xl"><ErrorBox msg={error} /></div>;
  if (!data || !form || !q) return <div className="p-4 text-[var(--text-secondary)]">Loading…</div>;

  const label = q.kind === 'proposal' ? 'Proposal' : 'Quote';
  // The numbers shown: only the view that answers the form as it is now (none while it is being fetched or after a failure).
  const view = viewFor(viewed, builderViewRequest(st, { revision: q.updated_at, basis, unsaved: dirty && canEdit }).key);
  // Which lines the scope sets and why it sets none (structure, not numbers): from the last view received
  // for this revision (none from an older one).
  const layout = viewLayout(viewed, q.updated_at);
  const rows = new Map((view?.lines || []).map((r) => [r.id, r]));
  const vt = view?.totals || null;
  const problems = view?.problems || [];
  const tools = toolsChoice(form);
  const total = vt ? vt.total : Number(q.total);
  const twoPartners = Number(total) > TWO_PARTNERS_OVER;
  const offerChip = { budgetary: 'bg-amber-100 text-amber-800', firm: 'bg-green-100 text-green-800' }[q.offer_type];
  // The lines whose quantities the scope sets (the server says which); every other line keeps its own inputs.
  const scopedIds = new Set(layout?.scoped_line_ids || []);
  // Why the scope sets no quantities (two per-sf lines, none), or that it did not load.
  const scopeNote = layout?.scope_note || (builderMade && !form.scope && viewErr ? `The scope did not load, so it cannot be changed here until it does; the saved quantities stand. ${/read access to Catalog/.test(viewErr) ? viewErr : 'Reload to try again.'}` : null);
  const lineEditor = (l) => (
    <LineRow l={l} editable={editable} products={products} productById={productById} builderMade={builderMade} scoped={builderMade && scopedIds.has(l.id)}
      set={(p) => setSt((s) => editLine(s, l.id, p))} remove={() => { setSt((s) => removeLine(s, l.id)); setOpenLine(null); }} price={() => priceLine(l.id)} />
  );

  return (
    <div className="mx-auto max-w-[96rem] pb-24">
      <Link to="/quotes" className="mb-2 inline-flex items-center gap-1 text-sm text-[var(--text-secondary)]"><ArrowLeft size={14} /> Quotes</Link>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-[var(--text-primary)]">{label} {q.number} V{q.version}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-[var(--text-secondary)]">
            <StatusChip status={q.display_status} />
            {offerChip && <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${offerChip}`}>{q.offer_type === 'firm' ? 'Firm' : 'Budgetary'}</span>}
            {q.ready_for_approval_at && q.status === 'draft' && <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-semibold text-indigo-800">Ready for approval</span>}
            {q.sent_at && <span>sent {easternDay(q.sent_at)} to {(q.sent_to || []).join(', ')}</span>}
            {q.first_viewed_at && <span>· viewed {easternDay(q.first_viewed_at)}</span>}
            {q.accepted_at && <span>· accepted {easternDay(q.accepted_at)} by {q.accepted_by_name}</span>}
            {!data.is_latest && <span className="text-amber-700">· a newer version exists</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className={btn2Cls} onClick={() => openAuthed(`/quotes/${id}/pdf`).catch((e) => setError(e.message))}><FileText size={13} className="mr-1 inline" />PDF</button>
          {editable && <button className={btn2Cls} disabled={busy || !dirty || pricing > 0} onClick={save}>{pricing > 0 ? 'Pricing…' : 'Save'}</button>}
          {q.status === 'draft' && data.is_latest && <button className={btnCls} disabled={busy || dirty || pricing > 0} onClick={approve} title={dirty ? 'Save first' : ''}><CheckCircle2 size={14} className="mr-1 inline" />Approve</button>}
          {q.status === 'approved' && <button className={btnCls} disabled={busy || dirty || pricing > 0} onClick={() => setConfirmSend(true)}><Send size={14} className="mr-1 inline" />{q.send_ambiguous ? 'Retry send' : 'Send…'}</button>}
          {data.public_url && <button className={btn2Cls} onClick={() => navigator.clipboard?.writeText(data.public_url).then(() => setNotice('Customer link copied.'))}><Copy size={13} className="mr-1 inline" />Customer link</button>}
          {['sent', 'viewed', 'declined', 'superseded', 'void'].includes(q.status) && data.is_latest && <button className={btn2Cls} disabled={busy} onClick={async () => { const r = await act('/new-version').catch(() => null); if (r) navigate(`/quotes/${r.quote.id}`); }}>New version</button>}
          {['draft', 'approved', 'sent', 'viewed'].includes(q.status) && <button className={btn2Cls} disabled={busy} onClick={() => { // eslint-disable-next-line no-alert
            if (window.confirm(`Void ${q.number} V${q.version}? The customer link will say it was withdrawn.`)) act('/void', {}, 'Voided.').catch(() => {}); }}>Void</button>}
          {q.status === 'accepted' && !data.order && <button className={btnCls} onClick={() => setConvert(true)}>Convert to order</button>}
          {data.order && <Link className={btnCls} to={`/orders/${data.order.id}`}>Order {data.order.number}</Link>}
        </div>
      </div>
      <ErrorBox msg={error} />
      {loadNotes.map((n) => <p key={n} className="mb-3 rounded bg-amber-50 px-3 py-2 text-sm text-amber-800">{n}</p>)}
      {notice && <p className="mb-3 rounded bg-green-50 px-3 py-2 text-sm text-green-800 dark:bg-green-900/20 dark:text-green-300">{notice}</p>}
      {q.send_error && <p className="mb-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">Last send failed: {q.send_error}</p>}
      {q.status === 'approved' && <p className="mb-3 text-xs text-[var(--text-secondary)]">Approved by {q.approved_by} · editing anything returns it to draft.</p>}

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_21rem]">
        <div className="min-w-0 space-y-4">
          <div role="tablist" aria-label="View" className="inline-flex gap-1 rounded-full bg-gray-100 p-1 dark:bg-slate-800">
            {[['ours', 'Our view'], ['customer', 'Customer preview']].map(([k, t]) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
                className={`rounded-full px-3 py-1.5 text-sm font-medium ${tab === k ? 'bg-white shadow-sm dark:bg-slate-900' : 'text-[var(--text-secondary)]'}`}>{t}</button>
            ))}
          </div>

          {tab === 'customer' ? (
            <CustomerPreview quoteId={id} revision={q.updated_at} dirty={dirty} onSave={editable ? save : null} saving={busy} />
          ) : (<>
            {meetingOn && meeting && (
              <MeetingReview review={meeting} choices={meetChoices} edits={meetEdits} msg={meetMsg} busy={busy} editable={canEdit}
                setChoice={(part, key, how) => setMeetChoices((c) => ({ ...c, [part]: { ...c[part], [key]: how } }))}
                setEdit={(key, v) => setMeetEdits((e) => ({ ...e, [key]: v }))}
                onAccept={acceptMeeting} onDefaults={meetingDefaults} onSaveCrm={saveMeetingCrm} onClose={() => setMeeting(null)}
                crmDraft={crmDraft(meeting, { meeting: meetChoices.meeting, web: meetChoices.web, edits: meetEdits })} />
            )}
            <section className="space-y-4 rounded-xl border border-[var(--border)] bg-white p-4 dark:bg-slate-900">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <Labeled t="Title"><input className={inputCls} disabled={!editable} value={fields.title} onChange={setF('title')} /></Labeled>
                <Labeled t={<span className="flex items-center gap-2">Customer name (printed) {T('fields.customer_name')}</span>}><input className={`${inputCls} ${changedRing(tagOf('fields.customer_name'))}`} disabled={!editable} value={fields.customer_name} onChange={setF('customer_name')} /></Labeled>
                <Labeled t={<span className="flex items-center gap-2">Account {T('fields.account_id')}</span>}><select className={inputCls} disabled={!editable} value={fields.account_id} onChange={setF('account_id')}><option value="">—</option>{accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></Labeled>
                <Labeled t={<span className="flex items-center gap-2">Contact {T('fields.contact_id')}</span>}><select className={inputCls} disabled={!editable} value={fields.contact_id} onChange={setF('contact_id')}><option value="">—</option>{shownContacts.map((c) => <option key={c.id} value={c.id}>{c.name}{c.email ? ` · ${c.email}` : ''}</option>)}</select></Labeled>
                <Labeled t={<span className="flex items-center gap-2">Recipient name {T('fields.recipient_name')}</span>}><input className={`${inputCls} ${changedRing(tagOf('fields.recipient_name'))}`} disabled={!editable} value={fields.recipient_name} onChange={setF('recipient_name')} /></Labeled>
                <Labeled t={<span className="flex items-center gap-2">Recipient email {T('fields.recipient_email')}</span>}><input className={`${inputCls} ${changedRing(tagOf('fields.recipient_email'))}`} disabled={!editable} type="email" value={fields.recipient_email} onChange={setF('recipient_email')} /></Labeled>
                <Labeled t="Bill-to / address block"><textarea className={inputCls} rows={2} disabled={!editable} value={fields.bill_to} onChange={setF('bill_to')} /></Labeled>
                <Labeled t="Bid due date"><input className={inputCls} type="date" disabled={!editable} value={fields.bid_due_date} onChange={setF('bid_due_date')} /></Labeled>
                <Labeled t="Valid for (days from bid due / issue)"><input className={inputCls} inputMode="numeric" disabled={!editable} value={fields.validity_days} onChange={setF('validity_days')} /></Labeled>
              </div>
              <div className="grid grid-cols-1 gap-4 border-t border-[var(--border)] pt-3 sm:grid-cols-2 lg:grid-cols-4">
                {extOn && (
                  <div>
                    <p className="mb-1 flex items-center gap-2 text-xs text-[var(--text-secondary)]">Offer type {T('fields.offer_type')}</p>
                    {/* "Firm" only on a quote that is firm already: only Make firm turns a quote firm (LP-442 r2). */}
                    <Radios name="qb-offer" disabled={!editable || !!q.sent_at} value={fields.offer_type} onChange={setF('offer_type')}
                      options={offerOptions(q.offer_type)} />
                    {q.sent_at && <p className="mt-1 text-[11px] text-[var(--text-secondary)]">Locked: a sent quote changes offer type only in a new version.</p>}
                  </div>
                )}
                <div>
                  <p className="mb-1 text-xs text-[var(--text-secondary)]">Type</p>
                  <Radios name="qb-kind" disabled={!editable} value={fields.kind} onChange={setF('kind')}
                    options={[{ value: 'quote', label: 'Quote' }, { value: 'proposal', label: 'Proposal', hint: 'Quote plus a scope narrative' }]} />
                </div>
                <div>
                  <p className="mb-1 text-xs text-[var(--text-secondary)]">Owner (reply-to)</p>
                  <Radios name="qb-owner" disabled={!editable} value={fields.owner_email} onChange={setF('owner_email')}
                    options={(meta?.partners || []).map((p) => ({ value: p.email, label: p.name, hint: p.public_email }))} />
                </div>
                <div>
                  <p className="mb-1 text-xs text-[var(--text-secondary)]">Sales tax</p>
                  <Radios name="qb-tax" disabled={!editable} value={fields.tax_mode} onChange={setF('tax_mode')}
                    options={[{ value: 'excluded', label: 'Excluded', hint: 'The default terms' }, { value: 'exempt_cert', label: 'Resale or exemption certificate on file' }, { value: 'charged', label: 'Charged' }]} />
                  {fields.tax_mode === 'charged' && <input className={`${inputCls} ml-6 mt-1 w-24`} placeholder="%" aria-label="Sales tax %" disabled={!editable} value={fields.tax_rate_pct} onChange={setF('tax_rate_pct')} />}
                </div>
              </div>
              {vt && (
                <div className="grid grid-cols-2 gap-2 border-t border-[var(--border)] pt-3 md:grid-cols-4">
                  <Tile label="Sell total" value={usd(vt.total)} sub={vt.per_sf != null ? `${usd(vt.per_sf, 2)} per sf` : null} />
                  <Tile label="Expected margin" value={pct(vt.expected.margin_pct)} sub="At catalog cost and the freight estimate" />
                  <Tile label="True margin" value={vt.true.margin_pct != null ? pct(vt.true.margin_pct) : 'Waiting on RFQ'}
                    sub={`${view.offer_ref ? `At offer ${view.offer_ref}` : "At Concria's RFQ answer"}${vt.true.margin_pct != null && vt.true.lines_without.length ? `; without ${vt.true.lines_without.join(', ')} (waiting on RFQ)` : ''}`} />
                  <Tile label="Lines to fix" value={String(problems.length)} bad={problems.some((p) => p.level === 'bad')}
                    sub={problems.length ? problems.map((p) => `${p.description}: ${p.text.toLowerCase()}`).join('; ') : 'Every line clears the floor'} />
                </div>
              )}
            </section>

            {builderMade && (form.scope || scopeNote) && (
              <ScopePanel scope={form.scope} sv={view?.scope} editable={editable} set={change} tagOf={tagOf} note={scopeNote} />
            )}

            <LinesTable lines={form.lines} off={form.off} rows={rows} totals={vt} problems={problems} basis={basis} setBasis={setBasis}
              offerRef={view?.offer_ref} floorRule={view?.floor_rule} editable={editable} setPrice={(lid, v) => change(`line:${lid}.unit_price`, v)}
              tools={tools} setTools={(c) => setSt((s) => setToolsChoice(s, c))} tagOf={tagOf}
              openId={openLine} setOpenId={setOpenLine} renderDetails={lineEditor} pending={viewPending} error={viewErr} />
            <section className="-mt-2 space-y-2 rounded-xl border border-t-0 border-[var(--border)] bg-white px-4 pb-4 pt-3 dark:bg-slate-900">
              {editable && (
                <div className="flex flex-wrap gap-2">
                  <button className={btn2Cls} onClick={() => { const l = newLine({ pricing: 'per_sf', dosage_lb_sf: '1.6' }); setSt((s) => addLine(s, l)); setOpenLine(l.id); }}><Plus size={12} className="mr-1 inline" />Material per sf</button>
                  <button className={btn2Cls} onClick={() => { const l = newLine(); setSt((s) => addLine(s, l)); setOpenLine(l.id); }}><Plus size={12} className="mr-1 inline" />Material per bag/pail</button>
                  <button className={btn2Cls} onClick={() => { const l = newLine({ type: 'freight', pricing: 'lump', description: 'Shipping' }); setSt((s) => addLine(s, l)); setOpenLine(l.id); }}><Plus size={12} className="mr-1 inline" />Freight</button>
                  <button className={btn2Cls} onClick={() => { const l = newLine({ type: 'tools', pricing: 'lump', description: 'Finishing tools', optional: true }); setSt((s) => addLine(s, l)); setOpenLine(l.id); }}><Plus size={12} className="mr-1 inline" />Tools (optional)</button>
                </div>
              )}
              <p className="text-xs text-[var(--text-secondary)]">Costs, margins and where they came from are internal: they never appear on the customer's copy.</p>
              {!dirty && data.warnings?.length > 0 && (
                <div className="space-y-1">{data.warnings.map((w) => (
                  <p key={`${w.line}${w.code}`} className={`flex items-start gap-1.5 text-xs ${w.severity === 'block' ? 'text-red-600' : w.severity === 'warn' ? 'text-amber-700' : 'text-[var(--text-secondary)]'}`}><AlertTriangle size={12} className="mt-0.5 shrink-0" />{w.message}</p>
                ))}</div>
              )}
            </section>

            {builderMade && form.costing && <CostInputsPanel costing={form.costing} cv={view?.costing} editable={editable} set={change} tagOf={tagOf} />}

            {builderMade && <BasisPanel basis={q.pricing_basis} view={view} />}

            <section className="space-y-3 rounded-xl border border-[var(--border)] bg-white p-4 dark:bg-slate-900">
              <div>
                <h2 className="text-sm font-semibold">{builderMade ? '5. ' : ''}Terms, and the scope the customer sees</h2>
                <p className="text-xs text-[var(--text-secondary)]">This is the text on the customer's PDF and page.</p>
              </div>
              {fields.kind === 'proposal' && (
                <Labeled t="Scope & approach (proposal narrative)"><textarea className={inputCls} rows={6} disabled={!editable} value={fields.narrative} onChange={setF('narrative')} /></Labeled>
              )}
              {extOn && (
                <div className={`rounded-lg ${tagOf('fields.scope_text') ? 'ring-2 ring-sky-300 dark:ring-sky-700' : ''}`}>
                  <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                    <p className="flex items-center gap-2 text-sm font-medium">Scope &amp; assumptions <span className="font-normal text-[var(--text-secondary)]">(printed on the quote)</span> {T('fields.scope_text')}</p>
                    {editable && <button type="button" className={btn2Cls} onClick={() => setEditScope(!editScope)}>{editScope ? 'Done' : 'Edit'}</button>}
                  </div>
                  {editScope && editable
                    ? <textarea className={inputCls} rows={10} value={fields.scope_text} onChange={setF('scope_text')} />
                    : fields.scope_text
                      ? <p className="whitespace-pre-wrap break-words rounded-lg bg-gray-50 p-3 text-sm dark:bg-slate-800">{fields.scope_text}</p>
                      : <p className="text-sm text-[var(--text-secondary)]">No scope block yet.</p>}
                  {editScope && <p className="mt-1 text-[11px] text-[var(--text-secondary)]">Save changes to keep it. Saving new lines refreshes the area and quantities in it.</p>}
                </div>
              )}
              <details className="rounded-lg border border-[var(--border)] p-3">
                <summary className="cursor-pointer text-sm font-semibold">Terms (default: the OLP terms)</summary>
                <textarea className={`${inputCls} mt-2 font-mono text-xs`} rows={14} disabled={!editable} value={fields.terms_text} onChange={setF('terms_text')} />
              </details>
              {extOn && (() => {
                const pv = termsChanged ? livePreview : { terms: data.terms_preview?.terms, notice: data.terms_preview?.notice };
                return (
                  <details className="rounded-lg border border-[var(--border)] p-3">
                    <summary className="cursor-pointer text-sm font-semibold">Terms &amp; Notes as the customer will see them{termsChanged ? ' (unsaved changes)' : ''}</summary>
                    {!pv ? <p className="mt-2 text-sm text-[var(--text-secondary)]">Updating preview…</p>
                      : pv.error ? <p className="mt-2 text-sm text-red-600">Preview unavailable: {pv.error}</p> : (<>
                        {pv.notice && <p className={`mt-2 rounded px-3 py-2 text-sm ${pv.notice.tone === 'warn' ? 'bg-amber-50 text-amber-900' : 'bg-green-50 text-green-800'}`}><b>{pv.notice.title}.</b> {pv.notice.text}</p>}
                        <p className="mt-2 whitespace-pre-wrap break-words text-xs">{pv.terms || 'No terms.'}</p>
                      </>)}
                  </details>
                );
              })()}
            </section>

            <section className="space-y-3 rounded-xl border border-[var(--border)] bg-white p-4 dark:bg-slate-900">
              <div>
                <h2 className="text-sm font-semibold">{builderMade ? '6. ' : ''}Approve and send</h2>
                <p className="text-xs text-[var(--text-secondary)]">Copilot can draft and mark ready. Approve and Send are a partner's click{twoPartners ? '; this quote is over $25,000, so two partners approve it (the portal records one partner\'s tap, so the second partner signs off too)' : '; over $25,000 needs two partners'}.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {q.status === 'draft' && data.is_latest && <button type="button" className={btnCls} disabled={busy || dirty || pricing > 0} onClick={approve} title={dirty ? 'Save first' : ''}><CheckCircle2 size={14} className="mr-1 inline" />Approve</button>}
                {q.status === 'approved' && <button type="button" className={btnCls} disabled={busy || dirty || pricing > 0} onClick={() => setConfirmSend(true)}><Send size={14} className="mr-1 inline" />{q.send_ambiguous ? 'Retry send' : 'Send…'}</button>}
              </div>
            </section>

            <section className="rounded-xl border border-[var(--border)] bg-white p-4 dark:bg-slate-900">
              <Labeled t="Internal notes (never sent)"><textarea className={inputCls} rows={2} disabled={!editable} value={fields.internal_notes} onChange={setF('internal_notes')} /></Labeled>
            </section>

            {data.versions.length > 1 && (
              <section className="rounded-xl border border-[var(--border)] bg-white p-4 dark:bg-slate-900">
                <h2 className="mb-2 text-sm font-semibold">Versions</h2>
                {data.versions.map((v) => (
                  <p key={v.id} className="flex items-center gap-2 text-sm"><Link className="text-primary" to={`/quotes/${v.id}`}>V{v.version}</Link> <StatusChip status={v.status} /> {usd(v.total)}</p>
                ))}
              </section>
            )}
          </>)}
        </div>

        <CopilotDock key={dockKey(q)} quote={q} open={dockOpen} onClose={() => setDockOpen(false)} beforeSend={beforeCopilot} onAnswer={afterCopilot} onUndo={undoChange}
          undoable={(cid) => !undoGate({ status: q.status, canEdit }) && !busy && canUndo(st, cid, destOf())} undoReason={(cid) => undoGate({ status: q.status, canEdit }) || undoBlock(st, cid, destOf())}
          undoNote={(cid) => (keepsBudgetary(st, cid) ? UNDO_KEEPS_BUDGETARY : null)}
          meeting={meetingOn && canEdit ? { onRead: readMeeting, feed: meetFeed } : null} />
      </div>

      {!dockOpen && (
        <button type="button" onClick={() => setDockOpen(true)} className="fixed bottom-4 right-4 z-40 flex items-center gap-2 rounded-full bg-slate-800 px-4 py-2 text-sm font-medium text-white shadow-lg lg:hidden">
          <Bot size={16} /> Copilot
        </button>
      )}

      {editable && dirty && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-[var(--border)] bg-white/95 p-3 text-center dark:bg-slate-900/95">
          <button className={btnCls} disabled={busy || pricing > 0} onClick={save}>{pricing > 0 ? 'Pricing…' : 'Save changes'}</button>
        </div>
      )}

      {confirmSend && (
        <Modal onClose={() => setConfirmSend(false)} title={`Send ${label.toLowerCase()} ${q.number} V${q.version}`}>
          <p className="text-sm">To <b>{q.recipient_email}</b>{q.cc_emails?.length ? `, cc ${q.cc_emails.join(', ')}` : ''}.</p>
          <p className="mt-1 text-sm">Total {usd(q.total)}. PDF attached, with a link to view and accept online.</p>
          <p className="mt-1 text-xs text-[var(--text-secondary)]">Replies go to the owner's Concria address.</p>
          <div className="mt-4 flex gap-2">
            {(dirty || pricing > 0) && <p className="mb-2 text-xs text-red-600">There are unsaved changes. Save (and approve again) before sending.</p>}
            <button className={btnCls} disabled={busy || dirty || pricing > 0} onClick={async () => {
              if (dirty || pricing > 0) return; // only the approved, saved revision is ever sent
              setConfirmSend(false); await act('/send', {}, 'Sent.').catch(() => {});
            }}>Send now</button>
            <button className={btn2Cls} onClick={() => setConfirmSend(false)}>Cancel</button>
          </div>
        </Modal>
      )}
      {convert && <ConvertModal quote={q} partners={meta?.partners || []} onClose={() => setConvert(false)} onDone={(o) => navigate(`/orders/${o.id}`)} />}
    </div>
  );
}

function Tile({ label, value, sub, bad = false }) {
  return (
    <div className={`min-w-0 rounded-lg border px-3 py-2 ${bad ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-900/20' : 'border-[var(--border)]'}`}>
      <p className="text-[10px] uppercase tracking-wide text-[var(--text-secondary)]">{label}</p>
      <p className={`text-lg font-semibold tabular-nums ${bad ? 'text-red-700 dark:text-red-300' : ''}`}>{value}</p>
      {sub && <p className="break-words text-[11px] text-[var(--text-secondary)]">{sub}</p>}
    </div>
  );
}

// The description the server would generate for this line (see quote-lines.js).
function autoDescription(l, productById, { raw = false } = {}) {
  const p = productById[l.product_id];
  if (!p) return '';
  // The server prints the dosage as a number ("2.0" → "2"); raw keeps what was typed.
  const d = raw ? l.dosage_lb_sf : parseNum(l.dosage_lb_sf);
  return l.pricing === 'per_sf' && l.dosage_lb_sf !== '' && (raw || Number.isFinite(d)) ? `${p.name} (${d} lbs/sqft)` : p.name;
}
// Units the app fills in by itself (the server defaults per_unit to the product's sale unit).
const AUTO_UNITS = new Set(['', 'sf', 'lot', 'ea']);

/**
 * The full line editor (opened with "Details" under a line). On a quote the builder made, the
 * quantities of the lines the scope sets (scoped: the one dry shake and the first Trowel Hard line, as
 * the server's view says) come from 1. Scope; the cost from the catalog or the landed estimate.
 */
function LineRow({ l, editable, products, productById, builderMade, scoped, set: rawSet, remove, price }) {
  const p = productById[l.product_id];
  const perSf = l.pricing === 'per_sf';
  const costFixed = builderMade && (!!l.product_id || l.type === 'freight');
  // Changing the product, pricing or dosage refreshes an auto-generated description; a
  // description the partner typed themselves is kept.
  const set = (patch) => {
    const next = { ...l, ...patch };
    const structural = 'product_id' in patch || 'dosage_lb_sf' in patch || 'pricing' in patch;
    const wasAuto = !l.description || l.description === autoDescription(l, productById) || l.description === autoDescription(l, productById, { raw: true });
    if (wasAuto && structural) patch = { ...patch, description: autoDescription(next, productById) };
    // A unit the app assigned (sf, the old product's sale unit, …) is cleared when the product or
    // pricing changes, so the server assigns the right one; a unit the partner typed is kept.
    const autoUnit = AUTO_UNITS.has(String(l.unit || '')) || (p && l.unit === p.sale_unit);
    if (autoUnit && ('product_id' in patch || 'pricing' in patch)) patch = { ...patch, unit: '' };
    rawSet(patch);
  };
  const [pa, pd, pw] = [parseNum(l.area_sf), parseNum(l.dosage_lb_sf), parseNum(l.waste_pct || 0)];
  const bags = perSf && p && p.unit_qty_uom === 'kg' && pa > 0 && pd > 0 && Number.isFinite(pw)
    ? Math.ceil((pa * pd * (1 + pw / 100) * 0.45359237) / Number(p.unit_qty) - 1e-9) : null;
  const pallets = bags == null ? null : Math.ceil(bags / (p.units_per_pallet || 48));
  const dis = !editable;
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-6">
        <div><p className="mb-1 text-xs text-[var(--text-secondary)]">Type</p>
          <Radios name={`qb-type-${l.id}`} disabled={dis} value={l.type} onChange={(v) => set({ type: v })} options={['material', 'freight', 'tools', 'service', 'other'].map((x) => ({ value: x, label: x }))} /></div>
        <div><p className="mb-1 text-xs text-[var(--text-secondary)]">Priced</p>
          <Radios name={`qb-pricing-${l.id}`} disabled={dis || scoped} value={l.pricing} onChange={(v) => set({ pricing: v })} options={[{ value: 'per_sf', label: 'per sf' }, { value: 'per_unit', label: 'per unit' }, { value: 'lump', label: 'lump sum' }]} /></div>
        <div className="col-span-2"><Labeled t="Catalog product"><select className={inputCls} disabled={dis} value={l.product_id} onChange={(e) => set({ product_id: e.target.value })}><option value="">— none —</option>{products.map((x) => <option key={x.id} value={x.id}>{x.sku} · {x.name}</option>)}</select></Labeled></div>
        <div className="col-span-2"><Labeled t="Description (printed)"><input className={inputCls} disabled={dis} value={l.description} placeholder={p ? p.name : ''} onChange={(e) => set({ description: e.target.value })} /></Labeled></div>
        {perSf ? (<>
          <Labeled t="Area (sf)"><input className={inputCls} inputMode="decimal" disabled={dis || scoped} value={l.area_sf} onChange={(e) => set({ area_sf: e.target.value })} /></Labeled>
          <Labeled t="Dosage lb/sf"><input className={inputCls} inputMode="decimal" disabled={dis || scoped} value={l.dosage_lb_sf} onChange={(e) => set({ dosage_lb_sf: e.target.value })} /></Labeled>
          <Labeled t="Waste %"><input className={inputCls} inputMode="decimal" disabled={dis || scoped} value={l.waste_pct} onChange={(e) => set({ waste_pct: e.target.value })} /></Labeled>
        </>) : l.pricing === 'per_unit' ? (<>
          <Labeled t="Qty"><input className={inputCls} inputMode="decimal" disabled={dis || scoped} value={l.qty} onChange={(e) => set({ qty: e.target.value })} /></Labeled>
          <Labeled t="Unit"><input className={inputCls} disabled={dis} value={l.unit} placeholder={p?.sale_unit || 'ea'} onChange={(e) => set({ unit: e.target.value })} /></Labeled>
          <div />
        </>) : <><div /><div /><div /></>}
        <Labeled t={perSf ? 'Price $/sf' : 'Price'}><input className={inputCls} inputMode="decimal" disabled={dis} value={l.unit_price} onChange={(e) => set({ unit_price: e.target.value })} /></Labeled>
        <Labeled t={perSf ? 'Cost $/sf (internal)' : 'Cost (internal)'}><input className={`${inputCls} bg-gray-50`} inputMode="decimal" disabled={dis || costFixed} title={costFixed ? (l.product_id ? 'Set from the catalog cost' : 'Set from the landed estimate') : undefined} value={l.unit_cost} placeholder="unknown" onChange={(e) => set({ unit_cost: e.target.value })} /></Labeled>
        <div className="flex flex-col justify-end text-right">
          <span className="text-[10px] uppercase text-[var(--text-secondary)]">Amount</span>
          <span className="font-semibold">{usd(lineAmount(l))}</span>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs text-[var(--text-secondary)]">
        {scoped && <span>Quantities are set in 1. Scope.</span>}
        {costFixed && <span>Cost comes from {l.product_id ? 'the catalog' : 'the landed estimate'}.</span>}
        {bags != null && <span>≈ {fmtNum(bags)} {countWord(bags, 'bag')} ({fmtNum(pallets)} {countWord(pallets, 'pallet')})</span>}
        <label className="flex items-center gap-1"><input type="checkbox" disabled={dis} checked={l.optional === true} onChange={(e) => set({ optional: e.target.checked })} /> optional (not in total)</label>
        <label className="flex items-center gap-1"><input type="checkbox" disabled={dis} checked={l.taxable !== false} onChange={(e) => set({ taxable: e.target.checked })} /> taxable</label>
        {editable && l.product_id && !costFixed && <button className="flex items-center gap-1 text-primary" onClick={price}><Calculator size={12} /> price from catalog</button>}
        {editable && <button className="ml-auto flex items-center gap-1 text-red-600" onClick={remove}><Trash2 size={12} /> remove</button>}
      </div>
    </div>
  );
}

function ConvertModal({ quote, partners, onClose, onDone }) {
  const [f, setF] = useState({ customer_po: '', concria_order_ref: '', sales_lead_email: quote.owner_email || '', delivery_lead_email: '', billing_email: quote.recipient_email || '', need_by_date: '', mode: 'create', shipment_id: '' });
  const [shipments, setShipments] = useState([]);
  const [shipNote, setShipNote] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/shipments?status=all').then((r) => setShipments(r.shipments || [])).catch(() => setShipNote('Could not load the shipments to link. Close and reopen this to try again.')); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  async function go(e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const body = { shipment: f.mode === 'link' ? { mode: 'link', shipment_id: f.shipment_id } : { mode: f.mode } };
      for (const k of ['customer_po', 'concria_order_ref', 'sales_lead_email', 'billing_email', 'need_by_date']) if (f[k]) body[k] = f[k];
      body.delivery_lead_email = f.delivery_lead_email || null;
      const r = await api.post(`/quotes/${quote.id}/convert`, body);
      // eslint-disable-next-line no-alert
      if (r.warnings?.length) window.alert(r.warnings.join('\n'));
      onDone(r.order);
    } catch (e2) { setErr(e2.message); } finally { setBusy(false); }
  }
  return (
    <Modal onClose={() => { if (!busy) onClose(); }} title={`Convert ${quote.number} V${quote.version} to an order`}>
      <form onSubmit={go}>
      {/* Locked while submitting: what is on screen is exactly what is sent. */}
      <fieldset disabled={busy} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Labeled t="Customer PO"><input className={inputCls} value={f.customer_po} onChange={set('customer_po')} /></Labeled>
        <Labeled t="Concria order / offer ref"><input className={inputCls} value={f.concria_order_ref} onChange={set('concria_order_ref')} /></Labeled>
        <Labeled t="Sales Lead (30%)"><select className={inputCls} value={f.sales_lead_email} onChange={set('sales_lead_email')}>{partners.map((p) => <option key={p.email} value={p.email}>{p.name}</option>)}</select></Labeled>
        <Labeled t="Delivery Lead (20%)"><select className={inputCls} value={f.delivery_lead_email} onChange={set('delivery_lead_email')}><option value="">None</option>{partners.map((p) => <option key={p.email} value={p.email}>{p.name}</option>)}</select></Labeled>
        <Labeled t="Invoices go to"><input className={inputCls} type="email" value={f.billing_email} onChange={set('billing_email')} /></Labeled>
        <Labeled t="Need on site by (pour date)"><input className={inputCls} type="date" value={f.need_by_date} onChange={set('need_by_date')} /></Labeled>
        <Labeled t="Shipment" wide>
          <div className="flex flex-wrap gap-2">
            <select className={`${inputCls} max-w-[14rem]`} value={f.mode} onChange={set('mode')}><option value="create">Create a new shipment</option><option value="link">Link an existing shipment</option><option value="none">No shipment yet</option></select>
            {f.mode === 'link' && <select className={inputCls} value={f.shipment_id} onChange={set('shipment_id')} required><option value="">Choose…</option>{shipments.map((s) => <option key={s.id} value={s.id}>{s.order_ref} · {s.project_name || s.customer_name || ''}</option>)}</select>}
            {f.mode === 'link' && shipNote && <p className="w-full text-sm text-amber-800">{shipNote}</p>}
          </div>
        </Labeled>
        <div className="sm:col-span-2"><ErrorBox msg={err} /><button className={btnCls} disabled={busy} type="submit">Create order</button></div>
      </fieldset>
      </form>
    </Modal>
  );
}
